import { useEffect, useRef } from 'react';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import {
  type ScholarshipClass,
  type ScholarshipPlayer,
} from '../../../../types/scholarships';
import {
  POSITION_FILTERS,
  positionToFilter,
  type PositionFilter,
} from './positionGroups';
import { classDisplayLabel, effectiveClass, orderClasses } from './scholarshipClasses';
import { positionLabel, type RosterSport } from './positionGroups';
import './ScholarshipFiltersModal.css';
import '../players/FilterChip.css';

interface Props {
  players: ScholarshipPlayer[];
  positionFilters: PositionFilter[];
  classFilters: ScholarshipClass[];
  onTogglePosition: (filter: PositionFilter) => void;
  onToggleClass: (playerClass: ScholarshipClass) => void;
  onClear: () => void;
  onClose: () => void;
  /**
   * Subset of POSITION_FILTERS to offer. Defaults to all eleven. The depth chart passes only
   * the positions the diamond can render (no 'Other'), so a coach cannot pick a filter that
   * always yields an empty board.
   */
  availablePositionFilters?: PositionFilter[];
  /** Passed rather than read from context so this stays renderable without an AuthProvider. */
  sport: RosterSport;
}

/**
 * Position and class filters for a roster, as a modal.
 *
 * Replaces the sidebar this used to be — a right-hand panel on the depth chart and an
 * expand-down region on the Players tab, with a collapsed state that had to be styled as
 * neither a panel nor a button. That collapsed state was the source of a run of layout bugs:
 * the aside kept the panel's padding and border, so the Filters control rendered as a bordered
 * box 58px tall next to 36px siblings. A modal has no collapsed state to get wrong, and it
 * gives the diamond the full width on the depth chart.
 *
 * Built in TagFilterModal's language — same overlay, header, sections and chips — because that
 * is the app's established filter modal and a second dialect would read as a different feature.
 *
 * Clicking the backdrop does not dismiss. A click handler on a non-interactive overlay trips
 * click-events-have-key-events and no-noninteractive-element-interactions, and re-baselining
 * the accessibility ratchet to buy a convenience is the wrong trade when Escape, the close
 * button and Done already dismiss. It also avoids the drag-release-over-backdrop bug the other
 * modals in this feature each had to guard against.
 *
 * Filters apply on click rather than on a Save. They write straight to the URL, which is what
 * makes a filtered view deep-linkable and what the session persistence reads; buffering them
 * behind a Save would mean holding two sources of truth for the length of the dialog. The
 * footer therefore says Done, not Apply — nothing is pending.
 */
export const ScholarshipFiltersModal = ({
  players,
  positionFilters,
  classFilters,
  onTogglePosition,
  onToggleClass,
  onClear,
  onClose,
  availablePositionFilters = POSITION_FILTERS,
  sport,
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  useEffect(() => {
    const trigger = (document.activeElement as HTMLElement) ?? null;
    return () => {
      trigger?.focus?.();
    };
  }, []);

  const positionCounts = new Map<PositionFilter, number>();
  const classCounts = new Map<ScholarshipClass, number>();
  for (const p of players) {
    const key = positionToFilter(p.position);
    positionCounts.set(key, (positionCounts.get(key) ?? 0) + 1);
    const playerClass = effectiveClass(p);
    if (playerClass != null) {
      classCounts.set(playerClass, (classCounts.get(playerClass) ?? 0) + 1);
    }
  }

  // Only the classes actually on the roster, in reading order: the eligibility ladder first,
  // then JC 1-3, then graduation years oldest first. Built from what is present rather than from a fixed
  // list, because a recruiting board's class years are not a closed set.
  const presentClasses = orderClasses(classCounts.keys());
  const activeCount = positionFilters.length + classFilters.length;

  /*
    Named rather than positional on purpose. `label` and `value` are both strings, so a
    positional call could -- and once did -- pass the stored position where the coach-facing
    label belongs, which renders the raw enum and typechecks cleanly.
  */
  const chip = ({
    value,
    label,
    count,
    selected,
    onClick,
  }: {
    /** Stable identity for this chip. Never rendered. */
    value: string;
    /** What the coach reads. For a position, always positionLabel(). */
    label: string;
    count: number;
    selected: boolean;
    onClick: () => void;
  }) => (
    <button
      key={value}
      type="button"
      role="checkbox"
      aria-checked={selected}
      className={`filter-chip${selected ? ' filter-chip--selected' : ''}`}
      onClick={onClick}
    >
      <span className="filter-chip__text">{label}</span>
      {/*
        The count is why these are hand-rolled rather than the shared FilterChip: knowing a
        position holds four players is most of the reason to filter by it, and FilterChip has
        nowhere to put it.
      */}
      <span className="scholarship-filters__chip-count">{count}</span>
    </button>
  );

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="scholarship-filters-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scholarship-filters-title"
    >
      <div className="scholarship-filters">
        <header className="scholarship-filters__header">
          <div>
            <h2 id="scholarship-filters-title" className="scholarship-filters__title">
              Filter players
            </h2>
            <p className="scholarship-filters__subtitle">
              {activeCount > 0
                ? `${activeCount} filter${activeCount === 1 ? '' : 's'} selected`
                : 'Select filters to narrow the roster'}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </header>

        <div className="scholarship-filters__body">
          <section className="scholarship-filters__section">
            {/*
              span + role="group" rather than a fieldset/legend: the chips are buttons, not
              inputs, so a legend would name a group of controls it has no association with.
            */}
            <div className="scholarship-filters__section-header">
              <span className="scholarship-filters__section-title" id="scholarship-filters-position">
                Position
              </span>
              <span className="scholarship-filters__section-subtitle">Select multiple</span>
            </div>
            <div
              className="scholarship-filters__chips"
              role="group"
              aria-labelledby="scholarship-filters-position"
            >
              {availablePositionFilters.map((filter) =>
                chip({
                  value: filter,
                  label: filter === 'Other' ? filter : positionLabel(filter, sport),
                  count: positionCounts.get(filter) ?? 0,
                  selected: positionFilters.includes(filter),
                  onClick: () => onTogglePosition(filter),
                }),
              )}
            </div>
          </section>

          {presentClasses.length > 0 && (
            <section className="scholarship-filters__section">
              <div className="scholarship-filters__section-header">
                <span className="scholarship-filters__section-title" id="scholarship-filters-class">
                  Class
                </span>
                <span className="scholarship-filters__section-subtitle">Select multiple</span>
              </div>
              <div
                className="scholarship-filters__chips"
                role="group"
                aria-labelledby="scholarship-filters-class"
              >
                {presentClasses.map((playerClass) =>
                  chip({
                    value: playerClass,
                    label: classDisplayLabel(playerClass),
                    count: classCounts.get(playerClass) ?? 0,
                    selected: classFilters.includes(playerClass),
                    onClick: () => onToggleClass(playerClass),
                  }),
                )}
              </div>
            </section>
          )}
        </div>

        <footer className="scholarship-filters__footer">
          {activeCount > 0 && (
            <button
              type="button"
              className="scholarship-filters__clear"
              onClick={onClear}
            >
              Clear all
            </button>
          )}
          <button
            type="button"
            className="scholarship-filters__done"
            onClick={onClose}
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
};
